<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import executiveSummaryAPI from '../../../api/executiveSummary';
import MetricCard from './MetricCard.vue';
import ReportEmailModal from './ReportEmailModal.vue';

const { t } = useI18n();

const SECTION_MARKER = '✦';

const STAGE_LABELS = [
  'exploring',
  'believing',
  'growing',
  'serving',
  'leading',
];

// Date range — default to current month
const now = new Date();
const toIso = d => d.toISOString().split('T')[0];
const startDate = ref(toIso(new Date(now.getFullYear(), now.getMonth(), 1)));
const endDate = ref(toIso(new Date(now.getFullYear(), now.getMonth() + 1, 0)));

const metrics = ref(null);
const isLoading = ref(false);
const isGenerating = ref(false);
const isSending = ref(false);
const emailModalOpen = ref(false);
const alertMsg = ref(null); // { type: 'success'|'error', text: '' }

const SPIRITUAL_STAGES = [
  'unknown',
  'curious',
  'seeker',
  'doubter',
  'believer',
  'backslider',
  'disciple',
  'leader',
];

const OUTREACH_STAGES = [
  { value: '1', label: 'Trust', description: 'First contact / greetings' },
  { value: '2', label: 'Pain', description: 'Loneliness, hopelessness' },
  { value: '3', label: 'Curiosity', description: 'God questions, purpose' },
  { value: '4', label: 'Muslim Identity', description: 'Faith & identity' },
  { value: '5', label: 'Challenge', description: 'Doctrinal objections' },
  {
    value: '6',
    label: 'Gospel Openness',
    description: 'Presenting the 4 truths',
  },
  { value: '7', label: 'Crisis', description: 'Safety first' },
];

const MOVEMENT_LABELS = {
  curiosity_growth: 'Curiosity Growing',
  doubt_emergence: 'Doubt / Questions',
  commitment_deepened: 'Commitment Deepened',
  faith_renewed: 'Faith Renewed',
  recommitment: 'Recommitment',
};

const totalSeekerBase = computed(() =>
  SPIRITUAL_STAGES.reduce(
    (sum, s) => sum + (metrics.value?.seeker_stage_breakdown?.[s] || 0),
    0
  )
);

const totalSpiritualConvs = computed(() =>
  OUTREACH_STAGES.reduce(
    (sum, s) =>
      sum +
      (metrics.value?.spiritual_pipeline_breakdown?.stages?.[s.value] || 0),
    0
  )
);

const maxSpiritualStageCount = computed(() =>
  Math.max(
    1,
    ...OUTREACH_STAGES.map(
      s => metrics.value?.spiritual_pipeline_breakdown?.stages?.[s.value] || 0
    )
  )
);

const totalDiscipleship = computed(() => {
  if (!metrics.value?.discipleship_pipeline) return 0;
  return Object.values(metrics.value.discipleship_pipeline).reduce(
    (a, b) => a + b,
    0
  );
});

function showAlert(type, text) {
  alertMsg.value = { type, text };
  setTimeout(() => {
    alertMsg.value = null;
  }, 5000);
}

async function fetchMetrics() {
  isLoading.value = true;
  try {
    const { data } = await executiveSummaryAPI.getMetrics({
      startDate: startDate.value,
      endDate: endDate.value,
    });
    metrics.value = data;
  } catch {
    showAlert(
      'error',
      'Failed to load metrics. Please check your connection and try again.'
    );
  } finally {
    isLoading.value = false;
  }
}

async function handleGeneratePdf() {
  isGenerating.value = true;
  try {
    const response = await executiveSummaryAPI.generatePdf({
      startDate: startDate.value,
      endDate: endDate.value,
    });
    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: 'application/pdf' })
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = `ministry_report_${startDate.value.slice(0, 7)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch {
    showAlert('error', 'PDF generation failed. Please try again.');
  } finally {
    isGenerating.value = false;
  }
}

async function handleSendEmail(emails) {
  isSending.value = true;
  try {
    await executiveSummaryAPI.sendByEmail({
      startDate: startDate.value,
      endDate: endDate.value,
      emails,
    });
    emailModalOpen.value = false;
    showAlert('success', 'Report queued! Emails will be delivered shortly.');
  } catch {
    showAlert('error', 'Failed to send. Please try again.');
  } finally {
    isSending.value = false;
  }
}

onMounted(fetchMetrics);
</script>

<template>
  <div class="p-6 max-w-5xl mx-auto">
    <!-- Page Header -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-[#1B2A4A]">
        {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.TITLE') }}
      </h1>
      <p class="text-sm text-slate-500 mt-1">
        {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.SUBTITLE') }}
      </p>
    </div>

    <!-- Alert -->
    <div
      v-if="alertMsg"
      :class="
        alertMsg.type === 'success'
          ? 'bg-green-50 border-green-400 text-green-800'
          : 'bg-red-50 border-red-400 text-red-800'
      "
      class="mb-4 border-l-4 p-3 text-sm rounded-r-lg"
    >
      {{ alertMsg.text }}
    </div>

    <!-- Controls row -->
    <div
      class="flex flex-wrap items-end gap-4 mb-6 p-4 bg-[#F5F3EE] rounded-xl border border-[#E8E6E0]"
    >
      <div class="flex flex-col gap-1">
        <label
          class="text-xs font-semibold text-slate-500 uppercase tracking-wide"
          >{{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.START_DATE') }}
        </label>
        <input
          v-model="startDate"
          type="date"
          class="border border-[#E8E6E0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C] bg-white"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label
          class="text-xs font-semibold text-slate-500 uppercase tracking-wide"
          >{{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.END_DATE') }}
        </label>
        <input
          v-model="endDate"
          type="date"
          class="border border-[#E8E6E0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C] bg-white"
        />
      </div>
      <button
        class="bg-[#1B2A4A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1B2A4A]/90 transition-colors"
        @click="fetchMetrics"
      >
        {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.APPLY') }}
      </button>
      <div class="flex gap-2 ml-auto">
        <button
          :disabled="isGenerating || isLoading"
          class="flex items-center gap-2 bg-[#C9A84C] text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
          @click="handleGeneratePdf"
        >
          <span class="i-lucide-file-down size-4" />
          {{
            isGenerating
              ? t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.GENERATING_PDF')
              : t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.DOWNLOAD_PDF')
          }}
        </button>
        <button
          :disabled="isLoading"
          class="flex items-center gap-2 border border-[#1B2A4A] text-[#1B2A4A] px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1B2A4A]/5 disabled:opacity-50 transition-colors"
          @click="emailModalOpen = true"
        >
          <span class="i-lucide-mail size-4" />
          {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.SEND_EMAIL') }}
        </button>
      </div>
    </div>

    <!-- Loading skeleton -->
    <div v-if="isLoading" class="space-y-6">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div
          v-for="n in 4"
          :key="n"
          class="bg-[#E8E6E0] rounded-lg h-24 animate-pulse"
        />
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div
          v-for="n in 3"
          :key="n"
          class="bg-[#E8E6E0] rounded-lg h-40 animate-pulse"
        />
      </div>
    </div>

    <template v-else-if="metrics">
      <!-- Section: Ministry at a Glance -->
      <div class="mb-6">
        <h2
          class="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2"
        >
          <span class="text-[#C9A84C]">{{ SECTION_MARKER }}</span>
          {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.GLANCE_HEADER') }}
        </h2>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard
            :label="t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.GOSPEL_CONVERSATIONS')"
            :value="metrics.gospel_conversations.current"
            :change="metrics.gospel_conversations.pct_change"
          />
          <MetricCard
            :label="t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.SALVATION_DECISIONS')"
            :value="metrics.salvation_decisions.current"
            :change="metrics.salvation_decisions.pct_change"
          />
          <MetricCard
            :label="t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.ACTIVE_DISCIPLES')"
            :value="metrics.active_disciples.current"
            :change="metrics.active_disciples.pct_change"
          />
          <MetricCard
            :label="
              t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.PRAYER_REQUESTS_LABEL')
            "
            :value="metrics.prayer_requests.total"
          />
        </div>
      </div>

      <!-- Section: Second row -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        <!-- Discipleship Pipeline -->
        <div class="bg-white border border-[#E8E6E0] rounded-xl p-5">
          <h3
            class="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1.5"
          >
            <span class="text-[#C9A84C]">{{ SECTION_MARKER }}</span>
            {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.PIPELINE_HEADER') }}
          </h3>
          <p class="text-[11px] text-slate-400 italic mb-3">
            {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.PIPELINE_CAPTION') }}
          </p>
          <div class="space-y-2.5">
            <div v-for="stage in STAGE_LABELS" :key="stage">
              <div class="flex justify-between text-xs mb-1">
                <span class="text-slate-600 capitalize font-medium">{{
                  stage
                }}</span>
                <span class="font-bold text-[#1B2A4A]">
                  {{
                    (metrics.discipleship_pipeline[stage] || 0).toLocaleString()
                  }}
                </span>
              </div>
              <div class="w-full bg-[#E8E6E0] rounded-full h-2">
                <div
                  class="bg-[#C9A84C] h-2 rounded-full transition-all duration-500"
                  :style="{
                    width:
                      totalDiscipleship > 0
                        ? ((metrics.discipleship_pipeline[stage] || 0) /
                            totalDiscipleship) *
                            100 +
                          '%'
                        : '0%',
                  }"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Top Flows -->
        <div class="bg-white border border-[#E8E6E0] rounded-xl p-5">
          <h3
            class="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1.5"
          >
            <span class="text-[#C9A84C]">{{ SECTION_MARKER }}</span>
            {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.TOP_FLOWS_HEADER') }}
          </h3>
          <div v-if="metrics.top_flows.length" class="space-y-1">
            <div
              v-for="flow in metrics.top_flows"
              :key="flow.name"
              class="flex justify-between items-center py-2 border-b border-[#F0EDE8] last:border-0"
            >
              <span class="text-xs text-slate-700 truncate pr-2">{{
                flow.name
              }}</span>
              <span class="text-xs font-bold text-[#1B2A4A] shrink-0">
                {{ flow.completed.toLocaleString() }}
              </span>
            </div>
          </div>
          <p v-else class="text-xs text-slate-400 italic">
            {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.NO_FLOWS') }}
          </p>
        </div>

        <!-- Prayer Ministry -->
        <div class="bg-white border border-[#E8E6E0] rounded-xl p-5">
          <h3
            class="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1.5"
          >
            <span class="text-[#C9A84C]">{{ SECTION_MARKER }}</span>
            {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.PRAYER_HEADER') }}
          </h3>
          <div class="space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-sm text-slate-500">{{
                t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.TOTAL_REQUESTS')
              }}</span>
              <span class="text-2xl font-bold text-[#1B2A4A]">
                {{ metrics.prayer_ministry.total.toLocaleString() }}
              </span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-slate-500">{{
                t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.ANSWERED')
              }}</span>
              <span class="text-xl font-bold text-green-600">
                {{ metrics.prayer_ministry.answered.toLocaleString() }}
              </span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-slate-500">{{
                t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.STILL_OPEN')
              }}</span>
              <span class="text-xl font-bold text-amber-600">
                {{ metrics.prayer_ministry.open.toLocaleString() }}
              </span>
            </div>
            <!-- Prayer answered progress bar -->
            <div class="w-full bg-[#E8E6E0] rounded-full h-2 mt-1">
              <div
                class="bg-green-500 h-2 rounded-full"
                :style="{
                  width:
                    metrics.prayer_ministry.total > 0
                      ? (metrics.prayer_ministry.answered /
                          metrics.prayer_ministry.total) *
                          100 +
                        '%'
                      : '0%',
                }"
              />
            </div>
            <p class="text-xs text-slate-400 text-right">
              {{
                t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.PCT_ANSWERED', {
                  pct:
                    metrics.prayer_ministry.total > 0
                      ? Math.round(
                          (metrics.prayer_ministry.answered /
                            metrics.prayer_ministry.total) *
                            100
                        )
                      : 0,
                })
              }}
            </p>
            <div
              class="border-t border-[#F0EDE8] pt-3 flex justify-between items-center"
            >
              <span class="text-sm text-slate-500">{{
                t(
                  'MINISTRY.REPORT.EXECUTIVE_SUMMARY.CRISIS_CONVERSATIONS_LABEL'
                )
              }}</span>
              <span class="text-xl font-bold text-red-600">
                {{
                  (
                    metrics.pastoral_care?.crisis_conversations || 0
                  ).toLocaleString()
                }}
              </span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-slate-500">{{
                t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.HANDOFF_TO_HUMAN_LABEL')
              }}</span>
              <span class="text-xl font-bold text-amber-600">
                {{
                  (
                    metrics.pastoral_care?.handoff_to_human_conversations || 0
                  ).toLocaleString()
                }}
              </span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-slate-500">{{
                t(
                  'MINISTRY.REPORT.EXECUTIVE_SUMMARY.CONTACTS_NEEDING_CARE_LABEL'
                )
              }}</span>
              <span class="text-xl font-bold text-amber-600">
                {{
                  (
                    metrics.pastoral_care?.contacts_with_crisis_history || 0
                  ).toLocaleString()
                }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Section: Reach by Channel + Geographic -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        <!-- Channel breakdown -->
        <div class="bg-white border border-[#E8E6E0] rounded-xl p-5">
          <h3
            class="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1.5"
          >
            <span class="text-[#C9A84C]">{{ SECTION_MARKER }}</span>
            {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.CHANNEL_HEADER') }}
          </h3>
          <div v-if="metrics.reach_by_channel.length" class="space-y-2.5">
            <div
              v-for="row in metrics.reach_by_channel.slice(0, 6)"
              :key="row.channel"
            >
              <div class="flex justify-between text-xs mb-1">
                <span class="text-slate-600 font-medium">{{
                  row.channel
                }}</span>
                <span class="text-slate-500">{{
                  `${row.pct}% (${row.count.toLocaleString()})`
                }}</span>
              </div>
              <div class="w-full bg-[#E8E6E0] rounded-full h-2">
                <div
                  class="bg-[#C9A84C] h-2 rounded-full"
                  :style="{ width: row.pct + '%' }"
                />
              </div>
            </div>
          </div>
          <p v-else class="text-xs text-slate-400 italic">
            {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.NO_CHANNEL_DATA') }}
          </p>
        </div>

        <!-- Geographic reach -->
        <div class="bg-white border border-[#E8E6E0] rounded-xl p-5">
          <h3
            class="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1.5"
          >
            <span class="text-[#C9A84C]">{{ SECTION_MARKER }}</span>
            {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.GEOGRAPHIC_HEADER') }}
          </h3>
          <div v-if="metrics.geographic_reach.length" class="space-y-1.5">
            <div
              v-for="(row, i) in metrics.geographic_reach.slice(0, 8)"
              :key="row.country"
              class="flex justify-between items-center py-1 border-b border-[#F0EDE8] last:border-0 text-xs"
            >
              <span class="text-slate-600">
                <span class="text-slate-400 mr-1.5">{{ `${i + 1}.` }}</span>
                {{ row.country }}
              </span>
              <span class="font-bold text-[#1B2A4A]">{{
                row.count.toLocaleString()
              }}</span>
            </div>
          </div>
          <p v-else class="text-xs text-slate-400 italic">
            {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.NO_GEOGRAPHIC_DATA') }}
          </p>
        </div>
      </div>

      <!-- Section: Conversation Sentiment -->
      <div class="bg-white border border-[#E8E6E0] rounded-xl p-5 mb-6">
        <h3
          class="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-1.5"
        >
          <span class="text-[#C9A84C]">{{ SECTION_MARKER }}</span>
          {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.SENTIMENT_HEADER') }}
        </h3>
        <div
          v-if="metrics.sentiment_breakdown?.total"
          class="grid grid-cols-3 gap-3"
        >
          <div
            class="bg-[#F5F3EE] rounded-lg p-3 border-t-2 border-t-green-600 text-center"
          >
            <p class="text-[10px] font-semibold text-slate-500 mb-1">
              {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.SENTIMENT_POSITIVE') }}
            </p>
            <p class="text-xl font-bold text-green-700">
              {{ (metrics.sentiment_breakdown.positive || 0).toLocaleString() }}
            </p>
          </div>
          <div
            class="bg-[#F5F3EE] rounded-lg p-3 border-t-2 border-t-slate-400 text-center"
          >
            <p class="text-[10px] font-semibold text-slate-500 mb-1">
              {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.SENTIMENT_NEUTRAL') }}
            </p>
            <p class="text-xl font-bold text-slate-600">
              {{ (metrics.sentiment_breakdown.neutral || 0).toLocaleString() }}
            </p>
          </div>
          <div
            class="bg-[#F5F3EE] rounded-lg p-3 border-t-2 border-t-red-500 text-center"
          >
            <p class="text-[10px] font-semibold text-slate-500 mb-1">
              {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.SENTIMENT_NEEDS_CARE') }}
            </p>
            <p class="text-xl font-bold text-red-600">
              {{
                (metrics.sentiment_breakdown.needs_care || 0).toLocaleString()
              }}
            </p>
          </div>
        </div>
        <p v-else class="text-xs text-slate-400 italic">
          {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.NO_SENTIMENT_DATA') }}
        </p>
      </div>

      <!-- Section: Seeker Base Detail (the headline spiritual journey funnel) -->
      <div class="bg-white border border-[#E8E6E0] rounded-xl p-5 mb-6">
        <h3
          class="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1 flex items-center gap-1.5"
        >
          <span class="text-[#C9A84C]">{{ SECTION_MARKER }}</span>
          {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.SEEKER_BASE_HEADER') }}
        </h3>
        <p class="text-[11px] text-slate-400 italic mb-4">
          {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.SEEKER_BASE_CAPTION') }}
        </p>
        <div class="grid grid-cols-4 md:grid-cols-8 gap-2">
          <div
            v-for="stage in SPIRITUAL_STAGES"
            :key="stage"
            class="bg-[#F5F3EE] rounded-lg p-3 border-t-2 text-center"
            :class="
              (metrics.seeker_stage_breakdown?.[stage] || 0) > 0
                ? 'border-t-[#1B2A4A]'
                : 'border-t-[#E8E6E0]'
            "
          >
            <p class="text-[10px] font-semibold text-slate-500 capitalize mb-1">
              {{ stage }}
            </p>
            <p
              class="text-xl font-bold"
              :class="
                (metrics.seeker_stage_breakdown?.[stage] || 0) > 0
                  ? 'text-[#1B2A4A]'
                  : 'text-slate-300'
              "
            >
              {{
                (metrics.seeker_stage_breakdown?.[stage] || 0).toLocaleString()
              }}
            </p>
          </div>
        </div>
        <p class="text-xs text-slate-400 text-right mt-2">
          {{
            t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.TOTAL_SEEKER_CONTACTS', {
              count: totalSeekerBase.toLocaleString(),
            })
          }}
        </p>
      </div>

      <!-- Section: Conversation Themes (per-conversation dynamics, not a journey stage) -->
      <div class="bg-white border border-[#E8E6E0] rounded-xl p-5 mb-6">
        <h3
          class="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1 flex items-center gap-1.5"
        >
          <span class="text-[#C9A84C]">{{ SECTION_MARKER }}</span>
          {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.SPIRITUAL_PIPELINE_HEADER') }}
        </h3>
        <p class="text-[11px] text-slate-400 italic mb-4">
          {{
            t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.SPIRITUAL_PIPELINE_CAPTION')
          }}
        </p>

        <div v-if="totalSpiritualConvs > 0" class="space-y-2 mb-5">
          <div v-for="stage in OUTREACH_STAGES" :key="stage.value">
            <div class="flex items-center justify-between text-xs mb-1">
              <span class="font-semibold text-[#1B2A4A] w-36 shrink-0">
                {{ stage.value }}. {{ stage.label }}
              </span>
              <span class="text-slate-400 flex-1 mx-3 truncate hidden sm:block">
                {{ stage.description }}
              </span>
              <span
                class="font-bold shrink-0"
                :class="
                  stage.value === '7' ? 'text-amber-600' : 'text-[#1B2A4A]'
                "
              >
                {{
                  (
                    metrics.spiritual_pipeline_breakdown?.stages?.[
                      stage.value
                    ] || 0
                  ).toLocaleString()
                }}
              </span>
            </div>
            <div class="w-full bg-[#E8E6E0] rounded-full h-2">
              <div
                class="h-2 rounded-full transition-all duration-500"
                :class="stage.value === '7' ? 'bg-amber-500' : 'bg-[#1B2A4A]'"
                :style="{
                  width:
                    ((metrics.spiritual_pipeline_breakdown?.stages?.[
                      stage.value
                    ] || 0) /
                      maxSpiritualStageCount) *
                      100 +
                    '%',
                }"
              />
            </div>
          </div>
        </div>
        <p v-else class="text-xs text-slate-400 italic mb-5">
          {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.NO_SPIRITUAL_DATA') }}
        </p>

        <!-- Stat boxes row -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div
            class="bg-[#F5F3EE] rounded-lg p-3 border-t-2 border-t-green-600 text-center"
          >
            <p class="text-[10px] font-semibold text-slate-500 mb-1">
              {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.GOSPEL_REACHED_LABEL') }}
            </p>
            <p class="text-xl font-bold text-green-700">
              {{
                (
                  metrics.spiritual_pipeline_breakdown?.gospel_reached || 0
                ).toLocaleString()
              }}
            </p>
          </div>
          <div
            class="bg-[#F5F3EE] rounded-lg p-3 border-t-2 border-t-amber-500 text-center"
          >
            <p class="text-[10px] font-semibold text-slate-500 mb-1">
              {{
                t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.PENDING_COMMITMENT_LABEL')
              }}
            </p>
            <p class="text-xl font-bold text-amber-600">
              {{
                (
                  metrics.spiritual_pipeline_breakdown?.pending_commitment || 0
                ).toLocaleString()
              }}
            </p>
          </div>
          <div
            class="bg-[#F5F3EE] rounded-lg p-3 border-t-2 border-t-[#1B2A4A] text-center"
          >
            <p class="text-[10px] font-semibold text-slate-500 mb-1">
              {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.WITH_SUMMARY_LABEL') }}
            </p>
            <p class="text-xl font-bold text-[#1B2A4A]">
              {{
                (
                  metrics.spiritual_pipeline_breakdown?.with_summary || 0
                ).toLocaleString()
              }}
            </p>
          </div>
          <div
            class="bg-[#F5F3EE] rounded-lg p-3 border-t-2 border-t-slate-400 text-center"
          >
            <p class="text-[10px] font-semibold text-slate-500 mb-1">
              {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.WITH_OBJECTIONS_LABEL') }}
            </p>
            <p class="text-xl font-bold text-slate-600">
              {{
                (
                  metrics.spiritual_pipeline_breakdown?.with_objections || 0
                ).toLocaleString()
              }}
            </p>
          </div>
        </div>
        <p class="text-xs text-slate-400 text-right mt-2">
          {{
            t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.SPIRITUAL_TOTAL', {
              count: totalSpiritualConvs.toLocaleString(),
            })
          }}
        </p>
      </div>

      <!-- Section: Testimonies -->
      <div v-if="metrics.testimonies?.length" class="mb-6">
        <h2
          class="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2"
        >
          <span class="text-[#C9A84C]">{{ SECTION_MARKER }}</span>
          {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.TESTIMONIES_HEADER') }}
        </h2>
        <div class="space-y-3">
          <div
            v-for="(item, i) in metrics.testimonies"
            :key="i"
            class="bg-white border border-[#E8E6E0] rounded-xl p-4 border-l-4 border-l-[#C9A84C]"
          >
            <div class="mb-2">
              <p class="font-semibold text-[#1B2A4A] text-sm">
                {{ item.contact_name }}
              </p>
              <div class="flex items-center gap-2 mt-0.5 flex-wrap">
                <span class="text-xs text-slate-400">{{
                  item.conversation_date
                }}</span>
                <span class="text-xs text-slate-400" aria-hidden="true">·</span>
                <span class="text-xs text-slate-500">{{ item.channel }}</span>
                <span
                  class="text-xs font-semibold px-1.5 py-0.5 rounded"
                  :class="
                    item.outcome === 'converted'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-amber-100 text-amber-700'
                  "
                >
                  {{
                    MOVEMENT_LABELS[item.spiritual_movement] ||
                    item.spiritual_movement
                  }}
                </span>
                <span
                  v-if="item.outcome === 'converted'"
                  class="text-xs font-bold text-green-700 bg-green-50 px-1.5 py-0.5 rounded border border-green-200"
                >
                  {{
                    t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.SALVATION_DECISION')
                  }}
                </span>
              </div>
            </div>
            <p class="text-sm text-slate-600 leading-relaxed">
              {{ item.summary }}
            </p>
          </div>
        </div>
      </div>

      <!-- Section: Outreach Highlights -->
      <div v-if="metrics.outreach_testimonies?.length" class="mb-6">
        <h2
          class="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2"
        >
          <span class="text-[#C9A84C]">{{ SECTION_MARKER }}</span>
          {{
            t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.OUTREACH_HIGHLIGHTS_HEADER')
          }}
        </h2>
        <div class="space-y-3">
          <div
            v-for="(item, i) in metrics.outreach_testimonies"
            :key="i"
            class="bg-white border border-[#E8E6E0] rounded-xl p-4"
            :class="
              item.badge === 'gospel_reached'
                ? 'border-l-4 border-l-green-500'
                : item.badge === 'pending_commitment'
                  ? 'border-l-4 border-l-amber-400'
                  : 'border-l-4 border-l-[#1B2A4A]'
            "
          >
            <div class="mb-2">
              <p class="font-semibold text-[#1B2A4A] text-sm">
                {{ item.contact_name }}
              </p>
              <div class="flex items-center gap-2 mt-0.5 flex-wrap">
                <span class="text-xs text-slate-400">{{
                  item.conversation_date
                }}</span>
                <span class="text-xs text-slate-400" aria-hidden="true">·</span>
                <span class="text-xs text-slate-500">{{ item.channel }}</span>
                <span
                  v-if="item.badge === 'gospel_reached'"
                  class="text-xs font-bold text-green-700 bg-green-50 px-1.5 py-0.5 rounded border border-green-200"
                >
                  {{
                    t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.BADGE_GOSPEL_REACHED')
                  }}
                </span>
                <span
                  v-else-if="item.badge === 'pending_commitment'"
                  class="text-xs font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200"
                >
                  {{
                    t(
                      'MINISTRY.REPORT.EXECUTIVE_SUMMARY.BADGE_PENDING_COMMITMENT'
                    )
                  }}
                </span>
                <span
                  v-else
                  class="text-xs font-semibold text-[#1B2A4A] bg-[#F5F3EE] px-1.5 py-0.5 rounded"
                >
                  {{
                    item.stage_label ||
                    t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.BADGE_STAGE', {
                      num: item.spiritual_stage,
                    })
                  }}
                </span>
              </div>
            </div>
            <p
              v-if="
                item.badge === 'pending_commitment' && item.pending_commitment
              "
              class="text-sm text-amber-700 italic mb-1"
            >
              {{
                t(
                  'MINISTRY.REPORT.EXECUTIVE_SUMMARY.PENDING_COMMITMENT_PREFIX'
                )
              }}: {{ item.pending_commitment }}
            </p>
            <p
              v-if="item.summary"
              class="text-sm text-slate-600 leading-relaxed"
            >
              {{ item.summary }}
            </p>
          </div>
        </div>
      </div>
      <div
        v-else-if="metrics && !metrics.outreach_testimonies?.length"
        class="mb-6 text-xs text-slate-400 italic"
      >
        {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.NO_OUTREACH_HIGHLIGHTS') }}
      </div>

      <!-- Team Highlights -->
      <div
        v-if="
          metrics.team_highlights.top_agent || metrics.team_highlights.top_flow
        "
        class="bg-[#1B2A4A] rounded-xl p-5 mb-6"
      >
        <h3
          class="text-xs font-bold text-[#C9A84C] uppercase tracking-widest mb-3 flex items-center gap-1.5"
        >
          {{ SECTION_MARKER }}
          {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.TEAM_HEADER') }}
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div v-if="metrics.team_highlights.top_agent" class="text-white">
            <p class="text-xs text-white/60 mb-0.5">
              {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.TOP_AGENT_LABEL') }}
            </p>
            <p class="font-semibold">
              {{ metrics.team_highlights.top_agent.name }}
            </p>
            <p class="text-[#C9A84C] text-sm">
              {{
                t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.CONVERSATIONS', {
                  count:
                    metrics.team_highlights.top_agent.count.toLocaleString(),
                })
              }}
            </p>
          </div>
          <div v-if="metrics.team_highlights.top_flow" class="text-white">
            <p class="text-xs text-white/60 mb-0.5">
              {{ t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.MOST_USED_FLOW') }}
            </p>
            <p class="font-semibold">
              {{ metrics.team_highlights.top_flow.name }}
            </p>
            <p class="text-[#C9A84C] text-sm">
              {{
                t('MINISTRY.REPORT.EXECUTIVE_SUMMARY.SESSIONS', {
                  count:
                    metrics.team_highlights.top_flow.count.toLocaleString(),
                })
              }}
            </p>
          </div>
        </div>
      </div>
    </template>

    <ReportEmailModal
      :is-open="emailModalOpen"
      :is-sending="isSending"
      @close="emailModalOpen = false"
      @send="handleSendEmail"
    />
  </div>
</template>
